import React, { FC, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardImg, CardText, CardTitle, Col, Container, Row } from 'reactstrap';

import Button from '../../components/Button/Button';
import TourDestinationList, {
  TourDestinationListProps,
} from '../../components/TourDestinations/TourDestinationList';
import UncontrolledSlider from '../../components/UncontrolledSlider/UncontrolledSlider';
import Helmet from '../../components/common/Helmet';
import { shuffle } from '../../helpers/shuffle';
import useTranslate from '../../hooks/useTranslate';
import {
  tourDestinationPathnameMap,
  tourDestinationPathnames,
} from '../../services/apis/tourDestinations/consts';

const ALL_TOURS_SECTION_ID = 'all-venues';
const TOP_TOURS = shuffle(tourDestinationPathnames);

const DestinationsPage: FC = () => {
  const { translate } = useTranslate();

  const tourDestinationItems = useMemo<TourDestinationListProps['items']>(
    () =>
      tourDestinationPathnames.map(pathname => ({
        id: tourDestinationPathnameMap[pathname],
        url: pathname,
        name: String(translate(`destinations.tours.${pathname}.name`)),
        description: String(translate(`destinations.tours.${pathname}.description`)),
        district: String(translate(`destinations.tours.${pathname}.district`)),
        logoUrl: require(`./images/${pathname}/logo.png`),
        imageUrl: require(`./images/${pathname}/image-1.jpg`),
      })),
    [translate]
  );

  const slides = useMemo(
    () =>
      TOP_TOURS.map(pathname => ({
        title: (
          <div key={pathname}>
            <h1 className="display-3 text-white text-uppercase fw-bold ">
              {translate('destinations.title')}
            </h1>
            <h2 className="display-6 text-white text-uppercase mb-5">
              {translate('destinations.subtitle')}
            </h2>
            <Link
              to={pathname}
              className="display-4 text-white text-uppercase mt-5 fw-bold fst-italic"
            >
              {translate(`destinations.tours.${pathname}.name`)}
            </Link>
          </div>
        ),
        description: null,
        image: require(`./images/${pathname}/slide.jpg`),
        // placeholder is generated with https://unpic-placeholder.netlify.app/
        placeholder: `data:image/bmp;base64,Qk1aBAAAAAAAADYAAAAoAAAABAAAAAMAAAABABgAAAAAACQAAAATCwAAEwsAAAAAAAAAAAAAW1A+TWFkXGhqiHp1eHpxcYeHgpOVqqelcYWEdY+ThaGkpLS1`,
      })),
    [translate]
  );

  const reasons = useMemo(
    () => [
      {
        image: require('./images/reasons/reason-1.jpg'),
        title: translate('destinations.reasons.reason-1.title'),
        description: translate('destinations.reasons.reason-1.description'),
      },
      {
        image: require('./images/reasons/reason-2.jpg'),
        title: translate('destinations.reasons.reason-2.title'),
        description: translate('destinations.reasons.reason-2.description'),
      },
      {
        image: require('./images/reasons/reason-3.jpg'),
        title: translate('destinations.reasons.reason-3.title'),
        description: translate('destinations.reasons.reason-3.description'),
      },
    ],
    [translate]
  );

  return (
    <>
      <Helmet
        titleTranslationKey={'destinations.meta.title'}
        descriptionTranslationKey={'destinations.meta.description'}
      />
      <UncontrolledSlider slides={slides}>
        <div className="text-center">
          <Button
            tag="a"
            href={`#${ALL_TOURS_SECTION_ID}`}
            primary
            size="lg"
            className="text-uppercase"
          >
            {translate('destinations.show-all-destinations-cta')}
          </Button>
        </div>
      </UncontrolledSlider>

      <Container className="my-5">
        <p>{translate('destinations.description')}</p>

        <section className="mt-5">
          <h3 className="h1 text-uppercase fw-bold">{translate('destinations.reasons.title')}</h3>
          <Row className="d-flex flex-column flex-lg-row mb-lg-5 gap-4">
            {reasons.map(({ image, title, description }) => (
              <Col>
                <Card className="my-sx-4 my-lg-0 h-100">
                  <CardImg alt="" src={image} top width="100%" />
                  <CardBody>
                    <CardTitle tag="h3" className="mb-3">
                      {title}
                    </CardTitle>
                    <CardText>{description}</CardText>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        <section id={ALL_TOURS_SECTION_ID} className="mt-5">
          <h3 className="h1 text-uppercase fw-bold">{translate('destinations.venues')}</h3>
          <TourDestinationList items={tourDestinationItems} />
        </section>
      </Container>
    </>
  );
};

export default DestinationsPage;
